import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import { SCROLL_OFFSET } from "./sections";

const FAQS = [
  {
    question: "Does Zero Typing AI work directly inside Google Docs?",
    answer:
      "Yes. Zero Typing AI runs on your desktop and works with Google Docs, letting you dictate directly into your document without switching tabs or copying text over from another app.",
  },
  {
    question:
      "Is Zero Typing AI more accurate than Google's built-in voice typing?",
    answer:
      "Yes. Zero Typing AI is built around a more accurate speech engine, so names, industry terms, and acronyms come through correctly far more often. That means less time proofreading and more time actually writing.",
  },
  {
    question: 'Do I need to speak punctuation commands like "comma" or "period"?',
    answer:
      "No. Zero Typing AI adds punctuation, capitalization, and paragraph breaks automatically, so you can talk the way you would explain something to a coworker without breaking your pace.",
  },
  {
    question: "Can I use Zero Typing AI outside of Google Docs too?",
    answer:
      "Yes. The same voice typing tool works across Gmail, Slack, and your favorite AI chat tools, so you get one consistent dictation experience everywhere you write.",
  },
  {
    question: "Is there a free way to try Zero Typing AI?",
    answer:
      "Yes. You can try Zero Typing AI free to see how it handles your own writing and vocabulary before deciding on a paid plan.",
  },
];

const CARD_SHADOW =
  "0px 10.73px 32.19px -12.88px #173A8C24, 0px 1.07px 2.15px 0px #0A0E190A";

const FAQSection = () => {
  // Single-open accordion; every item starts collapsed.
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section
      id="faqs"
      style={{ scrollMarginTop: `${SCROLL_OFFSET}px` }}
      className="w-full lg:max-w-[815.58px]"
    >
      <h2 className="font-jakarta font-bold text-[#0A0D16] text-[28px] sm:text-[36px] md:text-[44px] lg:text-[50px] leading-[1.28] lg:leading-[64px] tracking-[-0.8px]">
        Frequently Asked Questions
      </h2>

      <div className="pt-[25.76px]">
        <div
          className="overflow-hidden rounded-[17.17px] border-[1.34px] border-[#E6ECF5] bg-white"
          style={{ boxShadow: CARD_SHADOW }}
        >
          {FAQS.map(({ question, answer }, index) => {
            const isOpen = openIndex === index;
            const isLast = index === FAQS.length - 1;

            return (
              <div
                key={question}
                className={`transition-colors duration-200 ${
                  isLast ? "" : "border-b-[1.07px] border-[#E6ECF5]"
                } ${isOpen ? "bg-[#F7FAFF]" : "bg-white"}`}
              >
                <h3>
                  <button
                    type="button"
                    id={`faq-trigger-${index}`}
                    aria-expanded={isOpen}
                    aria-controls={`faq-panel-${index}`}
                    onClick={() => setOpenIndex(isOpen ? null : index)}
                    className="flex w-full cursor-pointer items-center justify-between gap-4 px-[16px] py-[18px] text-left sm:px-[21.46px] sm:py-[21.46px]"
                  >
                    <span className="font-jakarta text-[15px] font-bold leading-[24px] tracking-[0px] text-[#0A0D16] sm:text-[16.1px] sm:leading-[26.16px]">
                      {question}
                    </span>

                    <ChevronDown
                      className={`h-[18px] w-[18px] shrink-0 text-[#3AB1F6] transition-transform duration-300 ${
                        isOpen ? "rotate-180" : "rotate-0"
                      }`}
                      strokeWidth={2.2}
                    />
                  </button>
                </h3>

                {/*
                  grid-rows 0fr -> 1fr animates to the content's natural height
                  without measuring it, and keeps the answer in the DOM so it
                  stays indexable while collapsed.
                */}
                <div
                  id={`faq-panel-${index}`}
                  role="region"
                  aria-labelledby={`faq-trigger-${index}`}
                  className={`grid transition-all duration-300 ease-out ${
                    isOpen
                      ? "grid-rows-[1fr] opacity-100"
                      : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="overflow-hidden">
                    <p className="px-[16px] pb-[18px] font-jakarta text-[15px] font-normal leading-[24px] tracking-[0px] text-[#0A0D16B2] sm:px-[21.46px] sm:pb-[21.46px] sm:text-[16.1px] sm:leading-[26.16px]">
                      {answer}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
